/**
 * Push Notifications hook for PATIENT PORTAL (token-based, no auth).
 * Distinct from /hooks/usePushNotifications.js (which is for admin users).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

export const PUSH_STATES = {
  unsupported: "unsupported",
  default:     "default",
  denied:      "denied",
  granted:     "granted",
  subscribed:  "subscribed",
};

export const usePatientPush = (token) => {
  const [state, setState] = useState(PUSH_STATES.default);
  const [busy, setBusy]   = useState(false);

  const refresh = useCallback(async () => {
    if (typeof window === "undefined") return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
      setState(PUSH_STATES.unsupported);
      return;
    }
    if (Notification.permission === "denied") { setState(PUSH_STATES.denied); return; }
    try {
      const reg = await navigator.serviceWorker.ready;
      const existing = await reg.pushManager.getSubscription();
      if (existing) setState(PUSH_STATES.subscribed);
      else if (Notification.permission === "granted") setState(PUSH_STATES.granted);
      else setState(PUSH_STATES.default);
    } catch {
      setState(PUSH_STATES.default);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const subscribe = useCallback(async () => {
    if (!token) return false;
    setBusy(true);
    try {
      let reg = await navigator.serviceWorker.getRegistration();
      if (!reg) reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      const perm = await Notification.requestPermission();
      if (perm !== "granted") {
        setState(perm === "denied" ? PUSH_STATES.denied : PUSH_STATES.default);
        return false;
      }

      const { data } = await axios.get(`${API}/patient-portal/push/vapid-public-key`);
      const vapidKey = data?.public_key;
      if (!vapidKey) throw new Error("VAPID not configured");

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey),
      });
      const json = sub.toJSON();
      await axios.post(`${API}/patient-portal/public/${token}/push/subscribe`, {
        endpoint: json.endpoint,
        keys:     { p256dh: json.keys.p256dh, auth: json.keys.auth },
        user_agent: navigator.userAgent.slice(0, 200),
      });
      setState(PUSH_STATES.subscribed);
      return true;
    } catch (e) {
      console.error("[push] subscribe error", e);
      return false;
    } finally {
      setBusy(false);
    }
  }, [token]);

  const unsubscribe = useCallback(async () => {
    if (!token) return false;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        const ep = sub.endpoint;
        await sub.unsubscribe();
        try {
          await axios.post(`${API}/patient-portal/public/${token}/push/unsubscribe`, { endpoint: ep });
        } catch { /* tolerate */ }
      }
      setState(Notification.permission === "granted" ? PUSH_STATES.granted : PUSH_STATES.default);
      return true;
    } catch (e) {
      console.error("[push] unsubscribe error", e);
      return false;
    } finally {
      setBusy(false);
    }
  }, [token]);

  return { state, busy, subscribe, unsubscribe, refresh };
};
